import { useEffect, useRef, useState } from "react";

/**
 * Ambient gold particles for the hero. Cheap canvas, respects
 * prefers-reduced-motion, pauses when tab hidden.
 */
export default function Particles({ density = 55, className = "" }) {
    const canvasRef = useRef(null);
    const [enabled, setEnabled] = useState(true);

    useEffect(() => {
        setEnabled(!window.matchMedia("(prefers-reduced-motion: reduce)").matches);
    }, []);

    useEffect(() => {
        if (!enabled) return;
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext("2d");
        const DPR = Math.min(window.devicePixelRatio || 1, 2);
        const parent = canvas.parentElement;

        const resize = () => {
            const { clientWidth: w, clientHeight: h } = parent;
            canvas.width = w * DPR;
            canvas.height = h * DPR;
            canvas.style.width = w + "px";
            canvas.style.height = h + "px";
        };
        resize();

        const N = density;
        const particles = Array.from({ length: N }).map(() => ({
            x: Math.random() * canvas.width,
            y: Math.random() * canvas.height,
            r: (Math.random() * 1.4 + 0.3) * DPR,
            vx: (Math.random() - 0.5) * 0.18 * DPR,
            vy: (Math.random() - 0.5) * 0.18 * DPR,
            a: Math.random() * 0.5 + 0.15,
        }));

        let raf;
        const step = () => {
            ctx.clearRect(0, 0, canvas.width, canvas.height);
            particles.forEach((p) => {
                p.x += p.vx;
                p.y += p.vy;
                if (p.x < 0 || p.x > canvas.width) p.vx *= -1;
                if (p.y < 0 || p.y > canvas.height) p.vy *= -1;
                ctx.beginPath();
                ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
                ctx.fillStyle = `rgba(212,175,55,${p.a})`;
                ctx.fill();
            });
            raf = requestAnimationFrame(step);
        };
        raf = requestAnimationFrame(step);

        const ro = new ResizeObserver(resize);
        ro.observe(parent);
        const onVis = () => (document.hidden ? cancelAnimationFrame(raf) : (raf = requestAnimationFrame(step)));
        document.addEventListener("visibilitychange", onVis);

        return () => {
            cancelAnimationFrame(raf);
            ro.disconnect();
            document.removeEventListener("visibilitychange", onVis);
        };
    }, [enabled, density]);

    if (!enabled) return null;
    return (
        <canvas
            ref={canvasRef}
            aria-hidden
            className={`pointer-events-none absolute inset-0 h-full w-full ${className}`}
        />
    );
}
